/**
 * @file apiClient.js
 * @description Lightweight fetch-based API client used by all service modules.
 *              Provides GET, POST, PUT, PATCH, DELETE, and file download methods
 *              with centralised error handling. Every request carries the JWT
 *              from localStorage as x-auth-token (same as api/client.js), so
 *              auth-gated endpoints work no matter which service module calls.
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.mtandtiot.in/api';

// Auth header for every request — the backend reads x-auth-token.
const authHeaders = () => {
    const token = localStorage.getItem('token');
    return token ? { 'x-auth-token': token } : {};
};

/**
 * Expired / invalid session: drop the dead token and send the user to login,
 * matching api/client.js. Without this an expired token (they last 24h) left
 * auth-gated screens rendering a raw "API Error: 401 - Token is not valid"
 * over an empty page — which reads as lost data rather than "please log in
 * again". Only the auth-gated endpoints fail that way, since most routes have
 * no auth middleware, so the rest of a page keeps loading and hides the cause.
 */
const handleUnauthorized = () => {
    localStorage.removeItem('token');
    if (window.location.pathname !== '/login') {
        window.location.href = '/login';
    }
};

/**
 * Base API client for making fetch requests.
 */
const apiClient = {
    async get(endpoint) {
        try {
            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                headers: { ...authHeaders() },
            });
            if (!response.ok) {
                const errorText = await response.text();
                if (response.status === 401) handleUnauthorized();
                throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
            }
            return await response.json();
        } catch (error) {
            console.error(`GET ${endpoint} failed:`, error);
            throw error;
        }
    },

    async post(endpoint, data) {
        try {
            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const errorText = await response.text();
                if (response.status === 401) handleUnauthorized();
                throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
            }
            return await response.json();
        } catch (error) {
            console.error(`POST ${endpoint} failed:`, error);
            throw error;
        }
    },

    async put(endpoint, data) {
        try {
            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const errorText = await response.text();
                if (response.status === 401) handleUnauthorized();
                throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
            }
            return await response.json();
        } catch (error) {
            console.error(`PUT ${endpoint} failed:`, error);
            throw error;
        }
    },

    async patch(endpoint, data) {
        try {
            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify(data),
            });
            if (!response.ok) {
                const errorText = await response.text();
                if (response.status === 401) handleUnauthorized();
                throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
            }
            return await response.json();
        } catch (error) {
            console.error(`PATCH ${endpoint} failed:`, error);
            throw error;
        }
    },

    async delete(endpoint) {
        try {
            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                method: 'DELETE',
                headers: { ...authHeaders() },
            });
            if (!response.ok) {
                const errorText = await response.text();
                if (response.status === 401) handleUnauthorized();
                throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
            }
            return await response.json();
        } catch (error) {
            console.error(`DELETE ${endpoint} failed:`, error);
            throw error;
        }
    },

    async download(endpoint) {
        try {
            const response = await fetch(`${API_BASE_URL}${endpoint}`, {
                headers: { ...authHeaders() },
            });
            if (!response.ok) {
                const errorText = await response.text();
                if (response.status === 401) handleUnauthorized();
                throw new Error(`API Error: ${response.status} ${response.statusText} - ${errorText}`);
            }

            // Extract filename from Content-Disposition header
            const disposition = response.headers.get('Content-Disposition');
            let filename = 'download';
            if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            const blob = await response.blob();
            return { blob, filename };
        } catch (error) {
            console.error(`DOWNLOAD ${endpoint} failed:`, error);
            throw error;
        }
    }
};

export default apiClient;
